// Film Clubs (2026-07-31, Josh's spec; renamed + form split to /submit-screening
// + card restyle 2026-08-12) — the cordoned-off calendar for roving film clubs,
// bar screenings, and one-night pop-ups. DELIBERATELY separate from the main
// calendar (these aren't static theaters); their rows DO surface on film pages
// and in watchlist alerts (the promoter flywheel).
//
// Data: showtimes rows under cinema_id 'popup-events' (umbrella — each row's
// `theater` field carries the real location, `series` the presenting club).
// Rows are entered on approval (submissions via /submit-screening); the
// umbrella id is NOT in src/constants THEATERS → invisible on the main
// calendar by construction.
//
// CURATION POSTURE (Josh 8/12): listings lead; submission is a small button at
// the bottom. Design follows the site's Art Deco language — cream/gold,
// Cinzel titles, elevated cards.
import React, { useEffect, useState } from 'react';
import supabase from '../supabase';
import { isDevHost, demoEnabled, DEMO_EVENTS } from '../utils/popupDemo';
import { SEED_EVENTS } from '../utils/popupSeeds';
import { FILM_CLUB_FILTER, eventUrlPath, clubName, clubUrlPath } from '../utils/filmClubUrls';

const GOLD = '#8b6914';
const GOLD_LIGHT = '#d4a843';
const CREAM = '#faf7f2';
const INK = '#2a2520';

export default function FilmClubs() {
  const [events, setEvents] = useState([]);
  const [movies, setMovies] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  // Hand out the per-event link (/film-clubs/<event>) — native share sheet on
  // phones, clipboard fallback elsewhere. Real popup- rows only.
  const shareEvent = async (ev, mv) => {
    const url = window.location.origin + eventUrlPath(ev.id);
    const title = (mv && mv.title) || ev.cinema_movie_title;
    const club = clubName(ev.series);
    const shareTitle = club ? `${title} — ${club} | Film Revival NYC` : `${title} — Film Revival NYC`;
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, url }); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(ev.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) { window.prompt('Copy this link:', url); }
  };

  const isLocalDev = isDevHost();
  const demoMode = demoEnabled();

  useEffect(() => {
    (async () => {
      try {
        let rows;
        if (demoMode) {
          rows = DEMO_EVENTS;
        } else {
          const { data } = await supabase
            .from('showtimes')
            .select('*')
            .or(FILM_CLUB_FILTER)
            .gte('start_at', new Date().toISOString())
            .order('start_at');
          rows = data || [];
          // Dev preview of REAL pending seeds (kept out of the DB until Josh's
          // ship word — see utils/popupSeeds). Absent from any deployed build path.
          if (isLocalDev) {
            const have = new Set(rows.map((r) => r.id));
            rows = [...rows, ...SEED_EVENTS.filter((s) => !have.has(s.id) && new Date(s.start_at) >= new Date())]
              .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
          }
        }
        setEvents(rows || []);
        const ids = [...new Set((rows || []).map((r) => String(r.movie_id)))];
        if (ids.length) {
          const { data: mv } = await supabase.from('movies').select('id, title, slug, release_year, poster_path').in('id', ids);
          setMovies(Object.fromEntries((mv || []).map((m) => [String(m.id), m])));
        }
      } catch (e) { /* empty page is fine */ }
      setLoading(false);
    })();
  }, [demoMode, isLocalDev]);

  // group events by ET calendar day
  const byDay = {};
  for (const ev of events) {
    const day = new Date(ev.start_at).toLocaleDateString('en-US', {
      timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric',
    });
    (byDay[day] = byDay[day] || []).push(ev);
  }

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px 16px 40px', color: INK }}>
      <a href="/" style={{ color: GOLD, textDecoration: 'none', fontSize: '0.85rem' }}>← Back to calendar</a>

      {/* Header with the site's double-rule motif */}
      <div style={{ textAlign: 'center', margin: '20px 0 30px' }}>
        <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: '2rem', letterSpacing: '2px', margin: '0 0 6px' }}>Clubs &amp; Community Screenings</h1>
        <div style={{ width: '64px', height: '2px', background: GOLD_LIGHT, margin: '0 auto 4px' }} />
        <div style={{ width: '32px', height: '2px', background: GOLD_LIGHT, margin: '0 auto 12px' }} />
        <p style={{ color: '#6b6255', fontSize: '0.95rem', fontStyle: 'italic', margin: 0 }}>
          Repertory screenings from NYC's film clubs, libraries, and community venues.
        </p>
      </div>

      {demoMode && (
        <p style={{ background: '#fdf3d7', border: '1px solid #d4a834', borderRadius: '6px', padding: '8px 12px', fontSize: '0.8rem', color: '#6b5210' }}>
          Local demo — these sample events exist only in your browser, nothing is in the database.
        </p>
      )}
      {loading ? (
        <p style={{ color: '#6b6255', textAlign: 'center' }}>Loading…</p>
      ) : events.length === 0 ? (
        <div style={{ background: CREAM, border: '1px solid #d4cec4', borderRadius: '8px', padding: '24px', textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ margin: 0, color: '#6b6255' }}>No upcoming clubs &amp; community screenings listed — check back soon.</p>
        </div>
      ) : (
        Object.entries(byDay).map(([day, evs]) => (
          <div key={day} style={{ marginBottom: '26px' }}>
            <h2 style={{
              fontFamily: 'Cinzel, serif', fontSize: '0.95rem', letterSpacing: '2.5px',
              textTransform: 'uppercase', color: INK, borderBottom: `2px solid ${GOLD_LIGHT}`,
              paddingBottom: '6px', marginBottom: '14px',
            }}>{day}</h2>
            {evs.map((ev) => {
              const mv = movies[String(ev.movie_id)];
              const time = new Date(ev.start_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
              const poster = (mv && mv.poster_path) || ev.seedPoster;
              const title = mv && mv.title ? `${mv.title}${mv.release_year ? ` (${mv.release_year})` : ''}` : (ev.seedTitle || ev.cinema_movie_title);
              const isFree = /free/i.test(ev.linkLabel || '');
              return (
                <div key={ev.id} style={{
                  display: 'flex', gap: '14px', padding: '14px', marginBottom: '12px',
                  background: '#fdfbf7', border: '1px solid #e5e0d8', borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(42,37,32,0.06)',
                }}>
                  {poster ? (
                    <img src={poster} alt="" style={{ width: '72px', height: '108px', objectFit: 'cover', borderRadius: '4px', alignSelf: 'flex-start', boxShadow: '0 1px 4px rgba(42,37,32,0.25)' }} />
                  ) : (
                    <div style={{ width: '72px', height: '108px', borderRadius: '4px', background: '#1a1714', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', boxSizing: 'border-box', textAlign: 'center', flexShrink: 0, alignSelf: 'flex-start' }}>
                      <span style={{ fontFamily: 'Cinzel, serif', color: '#d4a843', fontSize: '0.55rem', lineHeight: 1.35, overflow: 'hidden' }}>{title}</span>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {ev.series && (
                      <div style={{ marginBottom: '5px' }}>
                        <a href={clubUrlPath(ev.series)} title={`All ${clubName(ev.series)} screenings`} style={{
                          fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                          background: 'rgba(212,168,67,0.15)', color: GOLD, border: `1px solid ${GOLD_LIGHT}`,
                          borderRadius: '10px', padding: '2px 9px', textDecoration: 'none', display: 'inline-block',
                        }}>{ev.series}</a>
                        {isFree && (
                          <span style={{
                            marginLeft: '6px', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
                            background: '#3d5c3a', color: '#fff', borderRadius: '10px', padding: '2px 9px',
                          }}>FREE</span>
                        )}
                      </div>
                    )}
                    <div style={{ fontFamily: 'Cinzel, serif', fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.25 }}>
                      {mv && mv.slug
                        ? <a href={`/films/${mv.slug}`} style={{ color: INK, textDecoration: 'none' }}>{title}</a>
                        : title}
                      {ev.seedTitle && <span style={{ marginLeft: '8px', fontSize: '0.6rem', fontFamily: 'inherit', fontWeight: 700, letterSpacing: '0.05em', background: '#fdf3d7', border: '1px solid #d4a834', color: '#6b5210', borderRadius: '4px', padding: '2px 6px', verticalAlign: 'middle' }}>PENDING — LOCAL ONLY</span>}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b6255', marginTop: '4px' }}>
                      <strong style={{ color: INK }}>{time}</strong> · {ev.theater}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {ev.booking_link && (
                        <a href={ev.booking_link} target="_blank" rel="noopener noreferrer"
                          style={{
                            display: 'inline-block', marginTop: '10px', padding: '6px 16px',
                            background: GOLD, color: '#fff', borderRadius: '4px', textDecoration: 'none',
                            fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.03em',
                          }}>
                          {ev.linkLabel || 'Tickets'}
                        </a>
                      )}
                      {!ev.seedTitle && !String(ev.id).startsWith('demo-') && (
                        <>
                          <a href={eventUrlPath(ev.id)}
                            style={{
                              display: 'inline-block', marginTop: '10px', padding: '6px 16px', background: 'none',
                              border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '4px', textDecoration: 'none',
                              fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.03em',
                            }}>
                            Event page
                          </a>
                          <button type="button" onClick={() => shareEvent(ev, mv)}
                            style={{
                              marginTop: '10px', padding: '6px 16px', background: 'none',
                              border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '4px',
                              fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.03em', cursor: 'pointer',
                            }}>
                            {copiedId === ev.id ? 'Link copied!' : 'Share'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* Small, quiet submission entry point (Josh 8/12: curation first —
          the intake form lives on its own page). */}
      <div style={{ textAlign: 'center', marginTop: '40px', paddingTop: '22px', borderTop: '1px solid #e5e0d8' }}>
        <p style={{ fontSize: '0.8rem', color: '#8a8272', margin: '0 0 8px' }}>
          Run a film club or hosting a repertory screening? Listings are reviewed and curated by hand.
        </p>
        <a href="/submit-screening"
          style={{ display: 'inline-block', padding: '6px 14px', border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '4px', textDecoration: 'none', fontSize: '0.8rem' }}>
          Submit your screening
        </a>
      </div>
    </div>
  );
}
